// @ts-check
import cookieFetch from './cookieFetch.js'
import { FOUR_HOURS, QUARTER_HOUR } from '../lib/durations.js'

/**
 * @typedef {{jwtId: string, jwt: string, startTime: number}} JwtInfo
 */

class SocketIo extends EventTarget {
  __destroyTimeout__

  destroyed = false

  manager = new IO.Manager({
    // transports: ['polling'],
    host: window.CONFIG.socket_host,
    path: window.CONFIG.socket_path
  })

  socket = this.createSocket('/')

  /** @type {Set<SocketIoClient>} */
  clients = new Set()

  constructor () {
    super()

    let markConnectionLostAt = null
    this.manager.on('reconnect_attempt', () => {
      this.socketStateChangedToDisconnect()

      if (markConnectionLostAt == null) {
        markConnectionLostAt = Date.now() + FOUR_HOURS
      } else if (markConnectionLostAt <= Date.now()) {
        this.socketStateChangedToNoConnectionPossible()
      }
    })

    this.manager.on('reconnect', () => {
      this.socketStateChangedToReconnected()
      markConnectionLostAt = null
    })
  }

  createSocket (nsp) {
    const socket = this.manager.socket(nsp, {
      auth: cb => this.loadJwtToken()
        .then(jwtInfo => { cb({ jwt: jwtInfo.jwt }) })
        .catch(() => { cb({}) })
    })

    socket.on('info', info => {
      if (info?.redBar === true) {
        this.socketStateChangedToNoConnectionPossible()
      }
    })

    return socket
  }

  /**
   * @return {Promise<JwtInfo>}
   */
  async loadJwtToken () {
    const now = Date.now()
    const jwtCache = this.#jwtCache
    if (jwtCache == null || jwtCache.expiredAt < now) {
      this.#jwtCache = {
        expiredAt: now + QUARTER_HOUR,
        promise: cookieFetch('/jwt-user-data').then(async response => {
          const data = await response.json()

          return {
            jwt: data.jwt,
            jwtId: data.id,
            startTime: data.startTime,
          }
        })
      }
    }
    return this.#jwtCache.promise
  }

  /** @type {?{expiredAt: number, promise: Promise<JwtInfo>}} */
  #jwtCache

  destroy () {
    this.destroyed = true
    this.socket.disconnect()
  }

  reconnectingFailedTries = 0
  hasConnection = true
  static disallowConnection = false

  static throwWhenWebsocketCrashed () {
    if (SocketIo.disallowConnection) {
      throw new Error('Sockets crashed. Reload to try again')
    }
  }

  socketStateChangedToReconnected () {
    if (SocketIo.disallowConnection) {
      return console.error('Could not open connection lost bar. Connections are already lost')
    }

    this.reconnectDebounce = setTimeout(() => {
      noConnectionContainer().hide()
      this.hasConnection = true
      this.dispatchEvent(new CustomEvent('connection', { detail: { state: 'green' } }))
      this.reconnectingFailedTries = 0
    }, 5_000)
  }

  socketStateChangedToDisconnect () {
    if (SocketIo.disallowConnection) {
      return console.error('Could not open connection lost bar. Connections are already lost')
    }

    clearTimeout(this.reconnectDebounce)
    noConnectionContainer().show()
    this.dispatchEvent(new CustomEvent('connection', { detail: { state: 'yellow' } }))
    this.hasConnection = false
  }

  socketStateChangedToNoConnectionPossible () {
    SocketIo.disallowConnection = true
    noConnectionContainer().hide()
    socketDisconnectedContainer().show()
    this.dispatchEvent(new CustomEvent('connection', { detail: { state: 'red' } }))
    this.destroy()
  }
}

export const socketDisconnectedContainer = () => document.getElementById('socket-disconnected')
export const noConnectionContainer = () => document.getElementById('socket-no-connection')

export class SocketIoClient {
  destroyed = false
  /** @type {SocketIo} */
  socketIo

  constructor () {
    SocketIo.throwWhenWebsocketCrashed()

    this.socketIo = window.__SOCKET_IO__ ??= new SocketIo()
    clearTimeout(this.socketIo.__destroyTimeout__)
    this.socketIo.clients.add(this)
  }

  destroy () {
    this.destroyed = true
    this.socketIo.clients.delete(this)
    if (this.socketIo.clients.size === 0) {
      this.socketIo.__destroyTimeout__ = setTimeout(() => {
        if (this.socketIo.clients.size !== 0) return
        this.socketIo.destroy()
        delete window.__SOCKET_IO__
      }, 15_000)
    }
  }
}
